import React, { useMemo, useEffect, useState, useRef } from 'react'
import {
  TableOfContents,
  TocItem,
  TocLink,
  TocSubItem,
  SliderTrack,
  SliderIndicator,
} from './atoms'

// 在服务端和客户端通用的 HTML 解析函数
// 客户端使用原生 DOMParser，服务端使用 jsdom
const createDocumentFromContent = (content: string): Document => {
  if (typeof window !== 'undefined' && window.DOMParser) {
    const parser = new window.DOMParser()
    return parser.parseFromString(content, 'text/html')
  }

  // Node 环境（SSR / build-html）
  // 使用 eval('require') 避免在浏览器打包阶段引入 jsdom 及其 Node 依赖
  // eslint-disable-next-line no-eval, @typescript-eslint/no-implied-eval
  const nodeRequire = eval('require') as (id: string) => {
    JSDOM: new (
      html: string,
      options?: {
        contentType?: string
      },
    ) => {
      window: {
        document: Document
      }
    }
  }
  const { JSDOM } = nodeRequire('jsdom')
  const dom = new JSDOM(content)
  return dom.window.document
}

export interface TocItemData {
  id: string
  text: string
  level: number
}

export interface TableOfContentsProps {
  content: string
}

const TOC_HEADING_SELECTOR = 'h2'

export const TableOfContentsComponent: React.FC<TableOfContentsProps> = ({ content }) => {
  const [activeId, setActiveId] = useState<string>('')
  const [isScrolling, setIsScrolling] = useState<boolean>(false)
  const isScrollingRef = useRef<boolean>(false)
  const linkRefs = useRef<Map<string, HTMLAnchorElement>>(new Map())
  const containerRef = useRef<HTMLElement | null>(null)
  const [sliderStyle, setSliderStyle] = useState<{ top: number; height: number } | null>(null)

  const tocItems = useMemo(() => {
    const doc = createDocumentFromContent(content)
    const headings = doc.querySelectorAll(TOC_HEADING_SELECTOR)
    const items: TocItemData[] = []

    headings.forEach((heading, index) => {
      const text = heading.textContent || ''
      const level = heading.tagName === 'H2' ? 2 : 3
      const id = `heading-${index}-${text
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '')}`

      // 为标题添加 id 属性
      heading.id = id

      items.push({ id, text, level })
    })

    return items
  }, [content])

  useEffect(() => {
    // 更新 DOM 中的标题 id
    if (typeof window === 'undefined') return

    // 等待内容渲染完成
    const timer = setTimeout(() => {
      const doc = createDocumentFromContent(content)
      const headings = doc.querySelectorAll(TOC_HEADING_SELECTOR)

      headings.forEach((heading, index) => {
        const text = heading.textContent || ''
        const id = `heading-${index}-${text
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '')}`

        // 在页面中查找对应的标题并设置 id
        const pageHeadings = document.querySelectorAll(TOC_HEADING_SELECTOR)
        if (pageHeadings[index]) {
          pageHeadings[index].id = id
        }
      })
    }, 100)

    return () => clearTimeout(timer)
  }, [content])

  useEffect(() => {
    // 同步 ref 和 state
    isScrollingRef.current = isScrolling
  }, [isScrolling])

  // 查找可滚动的父容器
  const findScrollableContainer = (element: HTMLElement | null): HTMLElement | null => {
    if (!element) return null

    let current: HTMLElement | null = element.parentElement
    while (current) {
      const style = window.getComputedStyle(current)
      const overflowY = style.overflowY || style.overflow
      const hasScrollableContent = current.scrollHeight > current.clientHeight

      if (
        (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
        hasScrollableContent
      ) {
        return current
      }

      current = current.parentElement
    }

    return null
  }

  // 更新滑块位置并确保高亮项在可视区域内
  useEffect(() => {
    if (!activeId) {
      setSliderStyle(null)
      return
    }

    const activeLink = linkRefs.current.get(activeId)
    if (activeLink) {
      const navContainer = containerRef.current || activeLink.closest('nav')
      if (navContainer) {
        const containerRect = navContainer.getBoundingClientRect()
        const linkRect = activeLink.getBoundingClientRect()
        const top = linkRect.top - containerRect.top
        const height = linkRect.height

        setSliderStyle({ top, height })

        // 查找可滚动的父容器（可能是父组件）
        const scrollableContainer = findScrollableContainer(navContainer)

        if (scrollableContainer) {
          // 计算 link 相对于可滚动容器的位置
          const containerRect_scroll = scrollableContainer.getBoundingClientRect()
          const linkRect_scroll = activeLink.getBoundingClientRect()

          // 计算 link 在容器中的相对位置
          const linkTopRelative =
            linkRect_scroll.top - containerRect_scroll.top + scrollableContainer.scrollTop
          const linkBottomRelative = linkTopRelative + linkRect_scroll.height
          const containerScrollTop = scrollableContainer.scrollTop
          const containerHeight = scrollableContainer.clientHeight

          // 计算 link 在可视区域中的位置
          const linkTopInView = linkTopRelative - containerScrollTop
          const linkBottomInView = linkBottomRelative - containerScrollTop

          // 如果 link 不在可视区域内，滚动容器使其可见
          const padding = 12 // 上下留一些边距
          if (linkTopInView < padding) {
            // link 在可视区域上方，向上滚动
            scrollableContainer.scrollTo({
              top: linkTopRelative - padding,
              behavior: 'smooth',
            })
          } else if (linkBottomInView > containerHeight - padding) {
            // link 在可视区域下方，向下滚动
            scrollableContainer.scrollTo({
              top: linkBottomRelative - containerHeight + padding,
              behavior: 'smooth',
            })
          }
        }
      }
    }
  }, [activeId, tocItems])

  useEffect(() => {
    if (tocItems.length === 0) {
      return
    }

    // 使用 IntersectionObserver 监听标题进入视口
    const observerOptions = {
      root: null,
      rootMargin: '-100px 0px -80% 0px', // 顶部100px偏移，底部80%视口高度
      threshold: [0, 0.1, 0.5, 1],
    }

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      // 如果正在滚动（点击触发的滚动），不更新高亮
      if (isScrollingRef.current) {
        return
      }

      // 找到所有可见的标题，选择最接近顶部的
      const visibleEntries = entries.filter((entry) => entry.isIntersecting)
      if (visibleEntries.length === 0) {
        return
      }

      // 按位置排序，选择最接近顶部100px的标题
      visibleEntries.sort((a, b) => {
        const aTop = a.boundingClientRect.top
        const bTop = b.boundingClientRect.top
        return Math.abs(aTop - 100) - Math.abs(bTop - 100)
      })

      const targetEntry = visibleEntries[0]
      const targetId = targetEntry.target.id

      if (targetId) {
        setActiveId(targetId)
      }
    }

    const observer = new IntersectionObserver(observerCallback, observerOptions)

    // 延迟设置 observer，确保 DOM 已渲染
    const setupTimer = setTimeout(() => {
      // 观察所有标题元素
      tocItems.forEach((item) => {
        const element = document.getElementById(item.id)
        if (element) {
          observer.observe(element)
        }
      })

      // 初始检查：手动检查一次当前可见的标题
      const checkInitialHighlight = () => {
        if (isScrollingRef.current) {
          return
        }

        const headings = tocItems.map((item) => {
          const element = document.getElementById(item.id)
          return { id: item.id, element }
        })

        let currentId = ''
        for (let i = headings.length - 1; i >= 0; i--) {
          const { id, element } = headings[i]
          if (element) {
            const rect = element.getBoundingClientRect()
            if (rect.top <= 100) {
              currentId = id
              break
            }
          }
        }

        if (currentId) {
          setActiveId(currentId)
        }
      }

      // 延迟初始检查，确保 DOM 已渲染
      setTimeout(checkInitialHighlight, 100)
    }, 300)

    return () => {
      clearTimeout(setupTimer)
      observer.disconnect()
    }
  }, [tocItems])

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const element = document.getElementById(id)
    if (element) {
      // 先设置滚动标志，阻止滚动过程中的高亮更新
      setIsScrolling(true)
      isScrollingRef.current = true

      const offset = 140 // 距离顶部的偏移量
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })

      // 检测滚动完成：使用防抖检测滚动停止
      let lastScrollTop = window.pageYOffset
      let scrollEndTimer: ReturnType<typeof setTimeout> | null = null
      let consecutiveStops = 0 // 连续停止次数
      const requiredStops = 3 // 需要连续3次检测到停止才认为滚动完成
      let isCleanedUp = false

      const cleanup = () => {
        if (isCleanedUp) return
        isCleanedUp = true

        window.removeEventListener('scroll', scrollListener)
        if (scrollEndTimer) {
          clearTimeout(scrollEndTimer)
        }

        // 滚动完成后，高亮点击的标题
        const targetElement = document.getElementById(id)
        if (targetElement) {
          setActiveId(id)
        }

        // 延迟清除滚动标志，恢复自动高亮
        setTimeout(() => {
          setIsScrolling(false)
          isScrollingRef.current = false
        }, 200)
      }

      const scrollListener = () => {
        const currentScrollTop = window.pageYOffset

        if (Math.abs(currentScrollTop - lastScrollTop) < 1) {
          // 滚动位置基本不变，增加停止计数
          consecutiveStops++
        } else {
          // 还在滚动，重置停止计数
          consecutiveStops = 0
          lastScrollTop = currentScrollTop
        }

        // 清除之前的定时器
        if (scrollEndTimer) {
          clearTimeout(scrollEndTimer)
        }

        // 如果连续多次检测到停止，认为滚动完成
        if (consecutiveStops >= requiredStops) {
          cleanup()
        } else {
          // 继续检测
          scrollEndTimer = setTimeout(() => {
            cleanup()
          }, 200) // 增加检测间隔，确保滚动完全停止
        }
      }

      window.addEventListener('scroll', scrollListener, { passive: true })

      // 备用：确保最终清除滚动状态并高亮
      setTimeout(() => {
        cleanup()
      }, 3000) // 增加超时时间，确保长距离滚动也能完成
    }
  }

  if (tocItems.length === 0) {
    return null
  }

  const setLinkRef = (id: string, element: HTMLAnchorElement | null) => {
    if (element) {
      linkRefs.current.set(id, element)
    } else {
      linkRefs.current.delete(id)
    }
  }

  return (
    <TableOfContents ref={(el) => (containerRef.current = el)}>
      <SliderTrack>
        {sliderStyle && (
          <SliderIndicator
            style={{
              transform: `translateY(${sliderStyle.top}px)`,
              height: `${sliderStyle.height}px`,
            }}
          />
        )}
      </SliderTrack>
      {tocItems.map((item) => {
        if (item.level === 2) {
          return (
            <TocItem key={item.id}>
              <TocLink
                ref={(el) => setLinkRef(item.id, el)}
                href={`#${item.id}`}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleClick(e, item.id)}
                $active={activeId === item.id}
              >
                {item.text}
              </TocLink>
            </TocItem>
          )
        } else {
          return (
            <TocSubItem key={item.id}>
              <TocLink
                ref={(el) => setLinkRef(item.id, el)}
                href={`#${item.id}`}
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => handleClick(e, item.id)}
                $active={activeId === item.id}
              >
                {item.text}
              </TocLink>
            </TocSubItem>
          )
        }
      })}
    </TableOfContents>
  )
}
